import type { Catalogue } from './fr.ts';

export const en: Catalogue = {
  'app.name': 'Tablo',
  'app.tagline': 'Electrical diagrams for installers',

  'menu.file': 'File',
  'menu.new': 'New',
  'menu.open': 'Open…',
  'menu.save': 'Save',
  'menu.saveAs': 'Save as…',
  'menu.export': 'Export',
  'menu.exportPdf': 'PDF (all sheets)',
  'menu.exportSvg': 'SVG (current sheet)',
  'menu.exportPng': 'PNG (current sheet)',
  'menu.print': 'Print…',
  'menu.projectInfo': 'Project information',
  'menu.demo': 'Example',
  'menu.benchmark': 'Performance test',
  'menu.undo': 'Undo',
  'menu.redo': 'Redo',
  'menu.fit': 'Fit to screen',
  'menu.language': 'Language',
  'menu.copy': 'Copy',
  'menu.cut': 'Cut',
  'menu.paste': 'Paste',
  'menu.duplicate': 'Duplicate',
  'menu.selectAll': 'Select all',

  'tool.select': 'Select',
  'tool.wire': 'Wire',
  'tool.text': 'Text',
  'tool.rotate': 'Rotate',
  'tool.mirror': 'Mirror',
  'tool.delete': 'Delete',

  'sheet.add': 'New sheet',
  'sheet.rename': 'Rename',
  'sheet.delete': 'Delete sheet',
  'sheet.confirmDelete': 'The sheet “{name}” and everything on it will be deleted.',
  'sheet.lastOne': 'A project must keep at least one sheet.',
  'sheetKind.single-line': 'Single-line diagram',
  'sheetKind.multi-line': 'Multi-line diagram',
  'sheetKind.control': 'Control diagram',
  'sheetKind.position-plan': 'Position plan',
  'sheetKind.board': 'Board layout',

  'status.elements': '{n} elements',
  'status.wires': '{n} wires',
  'status.zoom': 'Zoom {pct}%',
  'status.platform.desktop': 'Desktop',
  'status.platform.mobile': 'Mobile',
  'status.platform.web': 'Web',
  'status.saved': 'Saved',
  'status.unsaved': 'Unsaved changes',
  'status.autosaved': 'Backup copy at {time}',
  'status.exported': 'Exported: {name}',

  'panel.selection': 'Selection',
  'panel.noSelection': 'Nothing selected',
  'panel.multi': '{n} items selected',
  'panel.tag': 'Tag',
  'panel.tagDuplicate': 'This tag is already used',
  'panel.symbol': 'Symbol',
  'panel.position': 'Position (mm)',
  'panel.rotation': 'Rotation',
  'panel.mirrored': 'Mirrored',
  'panel.wire': 'Wire',
  'panel.wireLabel': 'Label / cable',
  'panel.wireLabelHint': 'e.g. XVB 3G2.5',
  'panel.sheet': 'Sheet',
  'panel.sheetName': 'Name',
  'panel.sheetKind': 'Diagram type',
  'panel.paper': 'Paper',
  'panel.orientation': 'Orientation',
  'panel.landscape': 'Landscape',
  'panel.portrait': 'Portrait',
  'panel.symbols': 'Symbols',
  'panel.search': 'Search symbols…',
  'panel.noResults': 'No symbols found',
  'panel.allSheets': 'All symbols',

  'cat.protection': 'Protection',
  'cat.distribution': 'Distribution',
  'cat.outlet': 'Outlets',
  'cat.appliance': 'Appliances',
  'cat.lighting': 'Lighting',
  'cat.command': 'Switches',
  'cat.control': 'Control',
  'cat.motor': 'Motors',
  'cat.signalling': 'Signalling',
  'cat.connection': 'Connection',
  'cat.earth': 'Earth',
  'cat.annotation': 'Annotation',

  'meta.title': 'Project name',
  'meta.ownerName': 'Owner',
  'meta.ownerAddress': 'Owner address',
  'meta.installationAddress': 'Installation address (if different)',
  'meta.author': 'Drawn by',
  'meta.company': 'Company',
  'meta.date': 'Date',
  'meta.earthingSystem': 'Earthing system',
  'meta.supplyVoltage': 'Supply voltage',
  'meta.locale': 'Drawing language',
  'meta.standard': 'Standard',
  'meta.standardHint': 'Rules the project checks follow.',
  'std.rgie': 'RGIE',
  'std.nfc15100': 'NF C 15-100',
  'std.nen1010': 'NEN 1010',
  'std.label.rgie': 'RGIE/AREI (Belgium)',
  'std.label.nfc15100': 'NF C 15-100 (France)',
  'std.label.nen1010': 'NEN 1010 (Netherlands)',
  'meta.hint': 'This information appears in the title block of every sheet.',

  'tb.project': 'Project',
  'tb.sheet': 'Sheet',
  'tb.sheetNo': 'No.',
  'tb.owner': 'Owner',
  'tb.date': 'Date',
  'tb.paper': 'Paper',
  'tb.installation': 'Installation address',
  'tb.supply': 'Voltage',
  'tb.earthing': 'Earthing',
  'tb.author': 'Drawn by / company',
  'tb.diagram': 'Diagram type',

  'dialog.ok': 'OK',
  'dialog.cancel': 'Cancel',
  'dialog.close': 'Close',
  'dialog.delete': 'Delete',
  'dialog.discard': 'Discard changes',
  'confirm.discard': 'Unsaved changes will be lost.',
  'confirm.discardTitle': 'Discard changes?',

  'recovery.title': 'Recover unsaved work?',
  'recovery.body': '“{name}” was changed on {date} and not saved.',
  'recovery.restore': 'Recover',
  'recovery.dismiss': 'Dismiss',
  'recovery.delete': 'Delete',
  'recovery.fromAccount': '(copy from your account)',
  'recovery.pick': 'Copy to recover',

  'bench.title': 'Performance test',
  'bench.running': 'Measuring… ({n} elements)',
  'bench.result': '{n} elements · {fps} fps · mean frame {avg} ms · p95 {p95} ms',

  'error.open': 'Could not open the file: {msg}',
  'error.save': 'Could not save: {msg}',
  'error.export': 'Could not export: {msg}',

  'hint.canvas': 'Drag: select · Wheel: zoom · Middle click, Space + drag or two fingers: pan',
  'hint.place': 'Click: place · R: rotate · Esc: done',
  'hint.wire':
    'Click a terminal to start · Click: corner · Double-click: finish · Backspace: undo corner · Esc: cancel',

  'tx.move': 'Move',
  'tx.add': 'Add {name}',
  'tx.delete': 'Delete',
  'tx.rotate': 'Rotate',
  'tx.mirror': 'Mirror',
  'tx.wire': 'Draw wire',
  'tx.paste': 'Paste',
  'tx.edit': 'Edit',
  'tx.sheetAdd': 'Add sheet',
  'tx.sheetDelete': 'Delete sheet',
  'tx.meta': 'Project information',
  'tool.room': 'Room',
  'tool.calibrate': 'Set scale',
  'panel.circuits': 'Circuits',
  'panel.checks': '{std} check',
  'panel.hide': 'Hide panel',
  'panel.background': 'Plan background',
  'circ.code': 'Circuit',
  'circ.label': 'Description',
  'circ.protection': 'Protection',
  'circ.rcd': 'RCD',
  'circ.cableType': 'Cable',
  'circ.section': 'mm²',
  'circ.cores': 'Cores',
  'circ.length': 'Length (m)',
  'circ.points': 'Points',
  'circ.legacy': 'Legacy',
  'circ.add': 'Add circuit',
  'circ.renumber': 'Number circuits',
  'circ.empty':
    'No circuits: add a breaker on a single-line diagram, import a board photo or ask the AI assistant.',
  'circ.mainBus': 'Main bus',
  'circ.kindSockets': 'Sockets',
  'circ.kindLights': 'Lighting',
  'circ.kindAppliance': 'Appliance',
  'circ.newLabel': 'New circuit',
  'circ.show': 'Show on the diagram',
  'circ.noSingleLine': 'Add a “Single-line diagram” sheet first.',
  'checks.ok': 'No problems found by the current rules.',
  'checks.summary': '{e} error(s), {w} warning(s), {i} info',
  'checks.unverified': 'rule to confirm',
  'checks.why': 'Why',
  'checks.fix': 'Fix',
  'checks.askAssistant': 'Fix with the assistant',
  'checks.goProjectInfo': 'Fill in project information',
  'checks.goMeasurements': 'Enter test results',
  'checks.goCircuits': 'Open the circuit table',
  'checks.goProperties': 'Edit its properties',
  'checks.goSheet': 'Sheet settings',
  'checks.goShow': 'Show on the drawing',
  'checks.goNewPlan': 'Create an empty position plan',
  'checks.goAddEarth': 'Add the earth electrode',
  'checks.goQuick': 'Fix it',
  'checks.quickMove30': 'Move under {tag} (30 mA)',
  'checks.quickTypeA': 'Make {tag} type A',
  'checks.quickMain300': 'Set {tag} to 300 mA',
  'checks.quickRating': 'Lower to {tag}',
  'checks.quickSection': 'Cable {tag} (rewiring)',
  'circ.estimated': 'Estimated from the plan: check on site',
  'checks.goRenumber': 'Number circuits',
  'checks.goNumberTerminals': 'Number terminals',
  'checks.goA3': 'Switch the sheet to A3',
  'checks.goEarthSockets': 'Replace with earthed sockets',
  'checks.goRename': 'Rename {from} to {tag}',
  'checks.goAttach': 'Connect to circuit…',
  'checks.goDrawRoom': 'Draw a room around it',
  'checks.goDelete': 'Delete it',
  'checks.goSpare': 'Mark as spare',
  'checks.goEstimate': 'Estimate from the plan',
  'checks.goPhoto': 'Read from a photo',
  'checks.estimated': 'Lengths estimated from the plan for {codes}: check them on site.',
  'checks.estimateNone':
    'No circuit could be measured on the plan (board and points needed on an imported floor plan).',
  'checks.photoRead': 'Read on the photo: {what}. Check it.',
  'checks.photoNone': 'Nothing readable on the photo.',
  'circ.spare': 'Spare',
  'measure.again': 'Measure again',
  'measure.previous': 'Before: {what} ({date})',
  'checks.attached': '{tag} connected to circuit {code}.',
  'checks.disclaimer': 'Helps prepare the inspection — does not replace the examination by an approved body.',
  'status.checks': '{std}: {e} error(s) · {w} warn.',
  'plan.import': 'Import a plan (image, PDF or DXF)…',
  'plan.replace': 'Replace plan…',
  'plan.remove': 'Remove background',
  'plan.opacity': 'Opacity',
  'plan.scale': 'Scale 1:',
  'plan.calibrate': 'Set scale…',
  'plan.calibrateHint': 'Click two points whose real distance you know (e.g. a wall).',
  'plan.distance': 'Real distance between the two points (m)',
  'plan.measured': 'Measured on the sheet: {mm} mm',
  'plan.toPlace': 'To place on the plan',
  'plan.allPlaced': 'All diagram points are placed.',
  'plan.importing': 'Importing plan…',
  'plan.noBackground': 'No background plan.',
  'ai.menu': 'AI',
  'ai.board': 'Import a board photo…',
  'ai.demoBanner':
    'Demo mode: the AI is not connected yet. The result below is a sample; the photo is not analysed.',
  'ai.demoText': 'Demo mode: simplified local parsing (no AI).',

  // Sign-in prompt for the AI features (signed out).
  'aiGate.title': 'AI features (beta) with a free Tablo account',
  'aiGate.intro':
    "The AI features are free during the beta; they only need you to be signed in. Here's what they do for you:",
  'aiGate.tab.assist': 'Assistant',
  'aiGate.tab.board': 'Board photo',
  'aiGate.tab.sketch': 'Sketch',
  'aiGate.tab.report': 'Inspection report',
  'aiGate.cap.assist':
    'Ask for a change in plain words: the assistant prepares it and you apply it in one click.',
  'aiGate.cap.board': 'Photograph an existing board: its RCDs and circuits are read for you.',
  'aiGate.cap.sketch': 'A hand-drawn single-line diagram becomes a clean, editable diagram.',
  'aiGate.cap.report': "Import an approved body's report: its remarks are picked up, ready to follow up.",
  'aiGate.signIn': 'Sign in or create an account',
  'aiGate.later': 'Not now',
  'ai.choosePhoto': 'Choose a photo…',
  'ai.analyse': 'Analyse',
  'ai.analysing': 'Analysing…',
  'ai.preview': 'Preview',
  'ai.previewHint': 'Hover the proposal to see it on the diagram; click to keep the preview.',
  'ai.previewBanner':
    'Preview of the proposal, not applied yet: {added} added, {changed} changed, {removed} removed',
  'ai.rechecking': 'Reading a rail again close up…',
  'ai.recheckNote': 'Rail {rail} read again close up: {labels} corrected.',
  'ai.review': 'Check every row before importing. Uncertain values are highlighted.',
  'ai.row': 'Rail',
  'ai.pos': 'Pos.',
  'ai.type': 'Device',
  'ai.rating': 'A',
  'ai.curve': 'Curve',
  'ai.sens': 'mA',
  'ai.rcdType': 'Type',
  'ai.label': 'Label',
  'ai.confidence': 'Confidence',
  'ai.import': 'Create the single-line diagram',
  'ai.notes': 'Notes',
  'ai.target': 'Add under',
  'ai.error': 'AI error: {msg}',
  'ai.unavailable':
    'The AI features (beta) are temporarily unavailable. Try again later: the rest of Tablo works as usual.',
  'ai.removeRow': 'Remove row',
  'ai.addRow': 'Add row',
  'ai.dt.main-switch': 'Main switch',
  'ai.dt.RCD': 'RCD',
  'ai.dt.MCB': 'Breaker',
  'ai.dt.RCBO': 'RCBO',
  'ai.dt.fuse': 'Fuse',
  'ai.dt.contactor': 'Contactor',
  'ai.dt.timer': 'Timer',
  'ai.dt.meter': 'Meter',
  'ai.dt.other': 'Other',
  'ai.toFill': 'Points and cables still to fill in: a photo does not show them.',
  'menu.reports': 'Reports',
  'menu.circuitsCsv': 'Circuit list (CSV)',
  'menu.bomCsv': 'Bill of materials (CSV)',
  'menu.labelsPdf': 'Board labels (PDF)',
  'menu.exportPdfFull': 'PDF + circuit list',
  'tx.calibrate': 'Set scale',
  'tx.background': 'Plan background',
  'tx.circuit': 'Edit circuit',
  'tx.addCircuit': 'Add circuit',
  'tx.renumber': 'Number circuits',
  'tx.aiImport': 'AI board import',
  'room.default': 'Room',
  'hint.room': 'Drag to draw a room · Esc: done',
  'hint.calibrate': 'Click the first point, then the second · Esc: cancel',
  'menu.diagram': 'Diagram',
  'menu.newFromTemplate': 'New from template…',
  'menu.terminalPlanCsv': 'Terminal plan (CSV)',
  'cmd.multiLine': 'Generate / update the multi-line diagram',
  'cmd.numberWires': 'Number the wires on this sheet',
  'cmd.numberTerminals': 'Number the terminals',
  'cmd.insert': 'Insert',
  'cmd.nothing': 'Nothing to change',
  'cmd.multiLineDone': 'Multi-line diagram “{name}” is up to date',
  'tpl.title': 'New project from a template',
  'tpl.create': 'Create',
  'ml.generated': 'Sheet generated from “{name}”: changes made by hand are lost when it is updated.',
  'ml.stale': 'The single-line diagram “{name}” has changed: this generated sheet is out of date.',
  'ml.update': 'Update',
  'tx.multiLine': 'Multi-line diagram',
  'tx.numberWires': 'Number wires',
  'tx.numberTerminals': 'Number terminals',
  'tx.template': 'Insert: {name}',
  'tx.assistant': 'AI assistant',
  'tx.sketch': 'Sketch import',
  'ai.assistant': 'Assistant',
  'ai.sketch': 'Sketch → diagram…',
  'ai.send': 'Send',
  'ai.askHint': 'E.g. “add an EV charger, then generate the multi-line diagram”',
  'ai.thinking': 'Thinking…',
  'ai.dictate': 'Dictate',
  'ai.stopDictation': 'Stop dictation ({s} s)',
  'ai.transcribing': 'Transcribing…',
  'ai.micDenied': 'Microphone access refused: allow it in the browser or device settings.',
  'ai.micMissing': 'No microphone found.',
  'ai.nothingHeard': 'Nothing heard: speak closer to the microphone.',
  'ai.apply': 'Apply',
  'ai.applied': 'Applied (undo with {mod}Z)',
  'ai.fbUp': 'Useful answer',
  'ai.fbDown': 'Wrong or unhelpful answer',
  'ai.fbWhat': 'What went wrong? (optional)',
  'ai.fbShare':
    'Share this conversation and the project (without names, addresses or photos) so it can become a test case',
  'ai.fbTrain': "Also use it to train Tablo's own AI model",
  'ai.fbShareHint':
    'Kept 12 months at most, only by Tablo. A model already trained with it is not retrained when it is deleted. See the privacy policy.',
  'ai.fbShareGood': 'Share this answer as a good example?',
  'ai.fbSend': 'Send',
  'ai.fbCancel': 'Cancel',
  'ai.fbThanks': 'Thanks for your feedback.',
  'ai.fbError': 'Feedback not sent: {msg}',
  'ai.skipped': 'Not applied: {msg}',
  'ai.proposal': 'Proposed changes',
  'ai.chooseSketch': 'Choose an image…',
  'ai.sketchHint': 'Photo or scan of a hand-drawn single-line diagram.',
  'ai.sketchResult': '{n} circuit(s) read, in {g} group(s).',
  'ai.mainRcd': 'Main RCD',
  'circ.method': 'Method',
  'circ.method.A1': 'A1 · conduit in thermally insulated wall',
  'circ.method.B1': 'B1 · in conduit (in or on a wall)',
  'circ.method.C': 'C · cable clipped to a wall',
  'circ.ib': 'Ib (A)',
  'circ.iz': 'Iz (A)',
  'circ.drop': 'ΔU',
  'circ.lmax': 'Max L',
  'circ.ibLoads': 'Calculated from the points’ power',
  'circ.ibRating': 'Power unknown: checked at the protection rating',
  'circ.dropLimit': 'Usual limit: {pct} %',
  'circ.lmaxHint': 'Maximum length protected against short circuits (conventional method)',
  'info.icc': 'Prospective Icc at the origin (kA)',
  'info.iccHint': 'Given by the DSO; used to check breaking capacities.',
  'menu.exportDxf': 'DXF (current sheet, for AutoCAD/QCAD…)',
  'tb.paperRev': 'Paper · rev.',
  'rev.menu': 'Revisions…',
  'rev.title': 'Revisions',
  'rev.index': 'Rev.',
  'rev.date': 'Date',
  'rev.author': 'By',
  'rev.description': 'Change',
  'rev.new': 'New revision',
  'rev.newHint': 'Freezes the current project as revision {index}.',
  'rev.create': 'Create revision {index}',
  'rev.none':
    'No revisions yet. Create revision A when you hand over the diagram (to the client, the inspection body…).',
  'rev.compare': 'Compare',
  'rev.restore': 'Go back to this state',
  'rev.restoreConfirm': 'Replace the project with the state of revision {index}? (Can be undone.)',
  'rev.diff': 'Compared with revision {index}: {added} added, {changed} changed, {removed} removed',
  'rev.diffClose': 'End comparison',
  'tx.revision': 'Revision {index}',
  'tx.restoreRevision': 'Back to revision {index}',
  'rev.table': 'Revisions',
  'menu.revisionsPdf': 'Revision table (in the full PDF)',
  'cat.user': 'My symbols',
  'sym.new': 'New symbol…',
  'sym.edit': 'Edit symbol',
  'sym.newTitle': 'New symbol',
  'sym.editTitle': 'Edit symbol',
  'sym.save': 'Save symbol',
  'sym.nameFr': 'Name (French)',
  'sym.nameEn': 'Name (English)',
  'sym.nameNl': 'Name (Dutch)',
  'sym.tagPrefix': 'Tag letter',
  'sym.width': 'Width (mm)',
  'sym.height': 'Height (mm)',
  'sym.views': 'Available in',
  'sym.hint': '1.25 mm grid. Drag to draw; “Connection point” adds or removes a point where wires attach.',
  'sym.tool.line': 'Line',
  'sym.tool.rect': 'Rectangle',
  'sym.tool.circle': 'Circle',
  'sym.tool.dot': 'Disc',
  'sym.tool.text': 'Text',
  'sym.tool.port': 'Connection point',
  'sym.tool.erase': 'Erase',
  'sym.problems': 'To complete: {list}',
  'sym.problem.name': 'name',
  'sym.problem.empty': 'drawing',
  'sym.problem.views': 'diagram types',
  'sym.problem.port': 'duplicate points',
  'sym.problem.port-outside': 'point outside the frame',
  'tx.newSymbol': 'New symbol: {name}',
  'tx.editSymbol': 'Symbol edited: {name}',
  'tx.importSymbols': 'Import symbols',
  'macro.title': 'Macros',
  'macro.none': 'Select a group of elements, then “Diagram → Save selection as macro”.',
  'macro.save': 'Save selection as macro…',
  'macro.name': 'Macro name',
  'macro.saved': 'Macro “{name}” saved',
  'macro.selectFirst': 'Select the elements to save first',
  'macro.insert': 'Insert at the centre of the view',
  'macro.delete': 'Delete macro',
  'macro.deleteConfirm': 'Delete the macro “{name}” from your library?',
  'lib.import': 'Import…',
  'lib.exportSymbols': 'Export my symbols',
  'lib.exportMacros': 'Export my macros',
  'lib.imported': '{n} item(s) imported',
  'lib.notLibrary': 'This file is not an Tablo library',
  'lib.symbolsFile': 'Tablo symbols',
  'lib.macrosFile': 'Tablo macros',
  'cat.ref': 'Manufacturer reference',
  'cat.none': '— none —',
  'cat.noMatch': 'No catalogue reference matches',
  'cat.modules': '{n} module(s)',
  'cat.source': 'source',
  'cat.mismatch': 'no longer matches the characteristics',
  'cat.assign': 'Catalogue references',
  'cat.assigned': '{n} {brand} reference(s) assigned; {missing} device(s) without a match',
  'tx.assignBrand': '{brand} references',
  'cmd.boardLayout': 'Generate / update the board layout',
  'tx.boardLayout': 'Board layout',
  'legal.privacy': 'Privacy policy',
  'legal.support': 'Support',
  'legal.registerNote': 'Your data stays in Europe; details in the',
  'about.menu': 'About Tablo…',
  'about.title': 'About',
  'about.beta':
    'Free beta. Features may still change; your .elec files stay readable by later versions. Please report any problem.',
  'about.data':
    'Your projects stay on your computer (or in your browser): nothing is sent to a server, except the photos and texts you submit to the AI.',
  'about.aiDemo': 'AI: demo mode (no data sent).',
  'about.aiRemote':
    'AI: photos and descriptions you send are processed then deleted; they are not used to train models.',
  'about.rules':
    'The RGIE check is an aid: it does not replace reading the RGIE nor the inspection by an approved body. Rules “to confirm” are flagged.',
  'about.checkUpdates': 'Check for updates',
  'about.update.unavailable': 'Automatic updates are not available in this build.',
  'about.update.up-to-date': 'Tablo is up to date.',
  'about.update.installed': 'Update installed.',
  'about.update.available': 'Update available.',
  'about.updateAvailable': 'Update available: {v}',
  'about.updateAvailableWord': 'available',
  'about.install': 'Install and restart',
  'welcome.title': 'Welcome to Tablo',
  'welcome.intro':
    'Tablo draws the diagrams of a Belgian electrical installation — single-line, position plan, multi-line, control, board layout — and checks them against the RGIE as you go.',
  'welcome.s1.title': 'The board',
  'welcome.s1.body':
    'start from a template, photograph the board (AI), or add circuits in the “Circuits” panel.',
  'welcome.s2.title': 'The points',
  'welcome.s2.body':
    'each circuit’s sockets, lighting and appliances, with cable lengths for the calculations.',
  'welcome.s3.title': 'The position plan',
  'welcome.s3.body':
    'import the floor plan (PDF, photo or DXF), scale it and place the points from the “To place” list.',
  'welcome.s4.title': 'The check',
  'welcome.s4.body': '“RGIE check” lists what is missing or wrong; click a line to go there.',
  'welcome.s5.title': 'The handover',
  'welcome.s5.body':
    'Diagram → multi-line and board layout; File → Revisions, then export the full PDF for the client and the inspection body.',
  'welcome.template': 'Start from a template…',
  'welcome.demo': 'Open the example',
  'welcome.empty': 'Empty project',
  'cloud.menu': 'Cloud',
  'cloud.open': 'Account and online projects…',
  'cloud.leave': 'Leave the online project (keep a local copy)',
  'cloud.title': 'Tablo online',
  'cloud.intro':
    'With an account, your projects are saved online, synced between your devices, and you can work together and share a read-only link.',
  'cloud.login': 'Sign in',
  'cloud.register': 'Create an account',
  'cloud.name': 'Name',
  'cloud.email': 'Email',
  'cloud.password': 'Password',
  'cloud.passwordHint': 'At least 8 characters.',
  'cloud.forgot': 'Forgot your password?',
  'cloud.forgotTitle': 'Forgot your password',
  'cloud.forgotIntro': 'Enter your account’s address: you will get a link to choose a new password.',
  'cloud.forgotSend': 'Send the link',
  'cloud.forgotSent':
    'If an account exists for {email}, an email is on its way. The link works for one hour; check your spam folder too.',
  'cloud.backToLogin': 'Back to sign in',
  'cloud.registerConfirmNote': 'We will email you a link to confirm your address.',
  'cloud.pendingTitle': 'Confirm your email address',
  'cloud.pendingBody': 'We sent a link to {email}. Open it to activate your account; it works for 48 hours.',
  'cloud.pendingSpam': 'Nothing arrived? Check your spam folder, or send the link again.',
  'cloud.resend': 'Send the link again',
  'cloud.resent': 'Link sent again. It replaces the previous one.',
  'cloud.pendingSignIn': 'Already confirmed? Sign in',
  'cloud.confirmTitle': 'Activating your account',
  'cloud.confirming': 'Activating…',
  'cloud.confirmExpired':
    'This link has expired or was already used. Sign in: if your address is not confirmed yet, you can get a new link.',
  'cloud.confirmRetry': 'Try again',
  'cloud.confirmed': 'Email address confirmed. Welcome to Tablo!',
  'cloud.resetTitle': 'New password',
  'cloud.resetIntro': 'Choose the new password for your account. Your other devices will be signed out.',
  'cloud.passwordTooShort': 'Password too short: at least 8 characters.',
  'cloud.newPassword': 'New password',
  'cloud.resetSave': 'Save and sign in',
  'cloud.resetExpired':
    'This link has expired or was already used. Ask for a new one with “Forgot your password?”.',
  'cloud.signedInAs': 'Signed in:',
  'cloud.logout': 'Sign out',
  'cloud.deleteAccount': 'Delete account…',
  'cloud.deleteTitle': 'Delete your account',
  'cloud.deleteIntro': 'This permanently deletes the account {email} and its data from the Tablo server:',
  'cloud.deleteWhat':
    'your online projects in your personal workspace and in the workspaces you are the only member of, with their photos, files, history, share links and templates, and your recovery copies;',
  'cloud.deleteShared':
    'in workspaces shared with others, the projects stay with the team; if you were the only owner, another member becomes owner;',
  'cloud.deleteLocal':
    'projects saved on this device stay on it. To keep an online project, open it and save it as a .tablo file first.',
  'cloud.deletePassword': 'Your password, to confirm',
  'cloud.deleteConfirm': 'Delete my account permanently',
  'cloud.deleteWrongPassword': 'Wrong password.',
  'cloud.deleteOffline': 'No connection to the server: the account can only be deleted online.',
  'cloud.deleted': 'Your account and its online data have been deleted.',
  'cloud.workspace': 'Workspace',
  'cloud.newWorkspace': 'New team workspace…',
  'cloud.create': 'Create',
  'cloud.tab.projects': 'Projects',
  'cloud.tab.members': 'Members',
  'cloud.tab.templates': 'Team templates',
  'cloud.upload': 'Save the current project to “{name}”',
  'cloud.noProjects': 'No projects in this workspace.',
  'cloud.untitled': 'Untitled',
  'cloud.personal': 'Personal workspace: create a team workspace to work together.',
  'cloud.role.owner': 'owner',
  'cloud.role.editor': 'editor',
  'cloud.role.viewer': 'viewer',
  'cloud.remove': 'Remove',
  'cloud.addMember': 'Add',
  'cloud.rolesHint':
    'Editors change projects. Viewers look. Owners also manage members. The person must create their account first.',
  'cloud.saveTemplate': 'Save the current project as a team template',
  'cloud.noTemplates': 'No team templates.',
  'cloud.brandTitle': 'Your diagrams,\non every site.',
  'cloud.brand1': 'Saved online and synced between computer, tablet and phone',
  'cloud.brand2': 'Work together in a team workspace',
  'cloud.brand3': 'Read-only link for the client and the inspection body',
  'cloud.dataEurope': 'Data hosted in Europe',
  'cloud.loginTitle': 'Sign in to Tablo',
  'cloud.registerTitle': 'Create a Tablo account',
  'cloud.loginIntro': 'Welcome back. Sign in to find your online projects.',
  'cloud.noAccount': 'No account yet?',
  'cloud.haveAccount': 'Already have an account?',
  'cloud.showPassword': 'Show password',
  'cloud.hidePassword': 'Hide password',
  'cloud.workspaces': 'Workspaces',
  'cloud.searchProjects': 'Search projects',
  'cloud.uploadHere': 'Save the current project here',
  'cloud.col.project': 'Project',
  'cloud.col.updated': 'Modified',
  'cloud.col.by': 'By',
  'cloud.col.template': 'Template',
  'cloud.openNow': 'open',
  'cloud.noMatch': 'No project matches “{q}”.',
  'cloud.you': 'you',
  'cloud.liveIn': 'Project open online in “{name}”',
  'cloud.liveOffline': 'Offline: changes will be sent when the connection is back',
  'cloud.menuProjects': 'Online projects…',
  'cloud.menuMembers': 'Members…',
  'cloud.menuTemplates': 'Team templates…',
  'cloud.accountMenu': 'Account',
  'cloud.emailPlaceholder': 'name@company.com',
  'cloud.roleOf': 'Role of',
  'cloud.memberEmail': 'New member’s email',
  'cloud.synced': '☁ Synced',
  'cloud.syncing': '☁ Sending… ({n})',
  'cloud.offline': '☁ Offline · {n} change(s) waiting',
  'cloud.with': 'with {names}',
  'cloud.readOnly': 'read-only',
  'cloud.rejected': 'Change “{label}” undone: another user changed these items.',
  'cloud.error': 'Server error: {msg}',
  'cloud.uploaded': 'Project saved to “{name}”; changes are synced.',
  'cloud.teamLibrary': 'Team library',
  'cloud.shareToTeam': 'Share my symbols and macros with the team',
  'cloud.sharedToTeam': 'Symbols and macros shared with “{name}”',
  'share.menu': 'Share…',
  'share.title': 'Share read-only',
  'share.intro':
    'A link lets the client, the architect or the inspection body view and download the drawings, without an account and without changing them.',
  'share.live': 'Always the latest version',
  'share.atRevision': 'Frozen at revision {index}',
  'share.create': 'Create link',
  'share.none': 'No links for this project.',
  'share.copy': 'Copy',
  'share.handover': 'Handover pack (PDF + QR code)',
  'share.revoke': 'Disable',
  'viewer.status': 'Read-only (shared link)',
  'viewer.live': 'Shared drawings — read-only',
  'viewer.revision': 'Drawings at revision {index} — read-only',
  'viewer.download': 'Download the PDF',
  'viewer.error': 'Invalid share link: {msg}',

  'dxf.title': 'Import a DXF plan',
  'dxf.info': 'DXF file {version} · {w} × {h} m',
  'dxf.units': 'Drawing unit',
  'dxf.unitsStated': 'stated in the file',
  'dxf.unitsGuessed': 'guessed from the size: please check',
  'dxf.scale': 'Scale',
  'dxf.paperSize': 'On the sheet: {w} × {h} mm',
  'dxf.tooBig': 'Larger than the drawing area: choose a smaller scale or an A3 sheet.',
  'dxf.layers': 'Layers',
  'dxf.layerCount': '{n} item(s)',
  'dxf.hiddenLayer': 'hidden in the file',
  'dxf.all': 'All',
  'dxf.none': 'None',
  'dxf.import': 'Import',
  'dxf.strays': '{n} isolated item(s) far from the drawing left out.',
  'dxf.skipped': 'Not imported: {list}.',
  'dxf.dwg':
    'DWG file (AutoCAD {version}): this closed format cannot be read directly. Save it as DXF from your CAD software (Save as → DXF) or convert it with ODA File Converter (free), then import the DXF.',
  'dxf.binary': 'Binary DXF is not supported: save it again as ASCII (text) DXF.',
  'dxf.notDxf': 'This file is not a readable DXF.',
  'dxf.empty': 'Nothing to import on the chosen layers.',
  'dxf.trueScale': 'The plan is at its true scale: no need to set the scale.',

  'quote.menu': 'Quote and bill of materials (Excel)…',
  'quote.title': 'Quote and bill of materials',
  'quote.intro':
    'Material from the single-line diagrams, cables from the circuit lengths and a labour line. Prices come from your (wholesaler) price list; everything stays editable in the file.',
  'quote.priceList': 'Price list',
  'quote.importPrices': 'Import a price list (CSV, Excel)…',
  'quote.priceListInfo': '{name} · {n} prices',
  'quote.noPriceList': 'No price list: prices are left to fill in in the file.',
  'quote.removePrices': 'Remove',
  'quote.noPrices': 'No prices found: the list needs a reference column and a price column.',
  'quote.vat': 'VAT',
  'quote.vat6': '6 % (renovating a dwelling over 10 years old)',
  'quote.vat21': '21 %',
  'quote.waste': 'Cable waste (%)',
  'quote.discount': 'Discount on material (%)',
  'quote.missingLengths': 'Cable not counted for {n} circuit(s) without a length: {list}.',
  'quote.lines': '{n} line(s), {priced} with a price',
  'quote.totals': 'Total excl. VAT {sub} · VAT {vat} · incl. VAT {total}',
  'quote.xlsx': 'Excel (.xlsx)',
  'quote.csv': 'CSV',
  'quote.fromSurvey':
    'The survey has not produced diagrams yet: generate them so the quote includes its material.',
  'quote.file': 'quote',
  'quote.colRef': 'Ref.',
  'quote.colDesc': 'Description',
  'quote.colQty': 'Qty',
  'quote.colPrice': 'Unit price',
  'quote.colSource': 'Source',
  'quote.sourceStale': '{name}: the last update failed; prices of {date} are used.',
  'quote.serverPricesDown':
    'The wholesalers’ prices could not be loaded (no connection): quote priced from the other sources.',
  'quote.sourceReference': 'Tablo reference prices (Belgium)',
  'quote.sourceUpdated': 'updated {date}',
  'quote.sourceOrder': 'Order used when a price is missing: {list}',
  'quote.preferredSource': 'Preferred price source',
  'quote.downloadPrices': 'Download the prices (Excel)…',
  'quote.priceListFile': 'Tablo prices.xlsx',
  'quote.useLibrary': 'Fill in missing prices from the Belgian reference prices (checked {date})',
  'quote.libraryPrice':
    'Tablo reference prices are public list prices excl. VAT: your wholesaler’s prices are lower.',
  'quote.labourHours': 'Labour (hours)',

  'survey.mode': 'Site survey',
  'survey.menu': 'Site survey (phone, tablet)',
  'survey.homeTitle': 'Surveys',
  'survey.intro':
    'Record the installation room by room and photograph the board: Tablo turns it into the single-line diagram and the position plans. Works with no network.',
  'survey.new': 'New survey',
  'survey.newTitle': 'Survey {date}',
  'survey.current': 'Open project',
  'survey.continue': 'Continue',
  'survey.onDevice': 'On this device',
  'survey.cloudTeam': 'Cloud — {team}',
  'survey.noProjects': 'No projects on this device.',
  'survey.offlineCopy': 'offline copy',
  'survey.cloudBadge': 'Cloud',
  'survey.remove': 'Remove from this device',
  'survey.removeConfirm': 'Remove “{name}” from this device? A cloud project stays on the server.',
  'survey.signIn': 'Sign in to the cloud',
  'survey.install': 'Install the app',
  'survey.installIos': 'To install it: Share button, then “Add to Home Screen”.',
  'survey.storageWarn': 'Install the app: otherwise the browser may erase the surveys kept on this device.',
  'survey.openEditor': 'Open in the editor',
  'survey.back': 'Back',
  'survey.tabRooms': 'Rooms',
  'survey.tabBoard': 'Board',
  'survey.tabChecks': 'Checks',
  'survey.tabInfo': 'Project',
  'survey.addRoom': 'Add a room',
  'survey.roomName': 'Room name',
  'survey.roomType': 'Room type',
  'survey.level': 'Floor',
  'survey.noRooms': 'Start with the room you are in.',
  'survey.points': 'Points',
  'survey.morePoints': 'More points…',
  'survey.noPoints': 'No points in this room: add them below.',
  'survey.circuit': 'Circuit',
  'survey.noCircuit': 'No circuit',
  'survey.chooseCircuit': 'Circuit of these points',
  'survey.newCircuit': 'New circuit',
  'survey.notes': 'Notes',
  'survey.photos': 'Photos',
  'survey.takePhoto': 'Photo',
  'survey.fromGallery': 'Gallery',
  'survey.removePhoto': 'Delete photo',
  'survey.deleteRoom': 'Delete room',
  'survey.deleteRoomConfirm': 'Delete “{name}” and its points?',
  'survey.pointCount': '{n} point(s)',
  'survey.unassigned': '{n} without circuit',
  'survey.addBoard': 'Add a board',
  'survey.mainBoard': 'Main board',
  'survey.boardName': 'Board name',
  'survey.boardPhotos': 'Board photos',
  'survey.readBoard': 'Read the board (AI)',
  'survey.reading': 'Reading the photo…',
  'survey.readResult': '{circuits} circuit(s) and {rcds} RCD(s) recognised.',
  'survey.readApply': 'Fill in the board',
  'survey.readKeeps': 'Circuits already recorded are updated in order and keep their points.',
  'survey.readError': 'Could not read it: {msg}',
  'survey.mainSwitch': 'Main switch',
  'survey.mainRcd': 'Main RCD',
  'survey.none': 'None',
  'survey.rcds': 'RCDs',
  'survey.addRcd': 'Add an RCD',
  'survey.rcdLabel': '{ma} mA RCD',
  'survey.mainBus': 'Directly under the main switch',
  'survey.circuits': 'Circuits',
  'survey.addCircuit': 'Add a circuit',
  'survey.circuitLabel': 'Description',
  'survey.circuitLabelPh': 'e.g. Kitchen sockets',
  'survey.device': 'Protection',
  'survey.devMCB': 'Breaker',
  'survey.devRCBO': 'RCBO',
  'survey.devFuse': 'Fuse',
  'survey.rating': 'Rating (A)',
  'survey.curve': 'Curve',
  'survey.poles': 'Poles',
  'survey.section': 'Section (mm²)',
  'survey.cableType': 'Cable',
  'survey.cores': 'Cores',
  'survey.length': 'Length (m)',
  'survey.rcdOf': 'RCD',
  'survey.legacy': 'Legacy part (before 1 October 1981)',
  'survey.circuitPoints': 'Points fed',
  'survey.deleteCircuit': 'Delete circuit',
  'survey.deleteCircuitConfirm': 'Delete circuit {code}? Its points stay, without a circuit.',
  'survey.deleteBoard': 'Delete board',
  'survey.deleteBoardConfirm': 'Delete “{name}” and its circuits?',
  'survey.deleteRcd': 'Delete RCD',
  'survey.earth': 'Earth electrode',
  'survey.earthOhm': 'Measured resistance (Ω)',
  'survey.sensitivity': 'Sensitivity (mA)',
  'survey.rcdType': 'Type',
  'survey.checksOk': 'Nothing to report.',
  'survey.fromDiagram': '{std} rule, on the diagrams that will be generated',
  'survey.generate': 'Generate diagrams',
  'survey.regenerate': 'Update the diagrams',
  'survey.generated': '{n} sheet(s) generated from the survey.',
  'survey.stale': 'The survey changed since the diagrams were generated.',
  'survey.upToDate': 'The diagrams match the survey.',
  'survey.notGenerated': 'No diagrams yet: generate them when the survey is complete (or now, to see).',
  'survey.regenerateWarn':
    'Sheets generated from the survey are replaced: changes made by hand on them are lost.',
  'survey.statusSaved': 'Saved',
  'survey.statusSynced': 'Synced',
  'survey.statusPending': 'To send: {n}',
  'survey.statusOffline': 'Offline',
  'survey.statusFiles': 'Photos to send: {n}',
  'survey.statusFile': 'File {name}',
  'survey.keepOnDevice': 'Keep on this device',
  'survey.kept': 'Project kept on this device.',
  'survey.pdf': 'Diagrams PDF',
  'survey.quantity': 'Quantity',
  'survey.deletePoint': 'Delete',
  'survey.upload': 'Put in the cloud ({team})',
  'survey.cloudSection': 'Cloud',
  'survey.inCloud': 'In the cloud — {team}',
  'survey.done': 'OK',

  // ---------- after the drawing: measurements, inspection reports, invoices ----------
  'menu.inspection': 'Inspection',
  'panel.photos': 'Photos',
  'quote.invoice': 'Invoice…',
  'survey.measure': 'Enter test results',
  'survey.pointPhotos': 'Photos of the point ({n})',
  'measure.menu': 'Test results…',
  'measure.earth': 'Earth electrode',
  'measure.earthOhm': 'Earth resistance (Ω)',
  'measure.title': 'Test results',
  'measure.intro':
    'One card per circuit and per RCD of the diagram. Each value is compared with its limit as soon as it is entered; the R-TEST rules report what fails or is missing.',
  'measure.date': 'Test date',
  'measure.circuits': 'Circuits',
  'measure.rcds': 'RCDs',
  'measure.insulation': 'Insulation (MΩ)',
  'measure.testVoltage': 'Test voltage',
  'measure.continuity': 'PE continuity (Ω)',
  'measure.loop': 'Fault loop Zs (Ω)',
  'measure.noLimit': 'fuse: no limit',
  'measure.tripMs': 'Trip time (ms)',
  'measure.tripMa': 'Trip current (mA)',
  'measure.testButton': 'Test button',
  'measure.trips': 'Trips',
  'measure.noTrip': 'Does not trip',
  'measure.nothing': 'No circuit or RCD on the diagram: draw or generate the single-line diagram first.',
  'measure.progress': '{m}/{c} circuits measured · {r}/{n} RCDs tested · {f} failed',
  'measure.csv': 'Export (CSV)',
  'measure.file': 'test results',
  'measure.tx': 'Test {ref}',
  'inspection.menu': 'Inspection reports and remarks…',
  'inspection.title': 'Inspection reports',
  'inspection.read': 'Read a report (photo or PDF)…',
  'inspection.reading': 'Reading the report…',
  'inspection.readFailed': 'Could not read the report: {error}',
  'inspection.manual': 'Enter by hand',
  'inspection.none':
    'No inspection report for this project yet. Read the approved body’s report, or enter its remarks by hand.',
  'inspection.pick': 'Report',
  'inspection.body': 'Approved body',
  'inspection.reference': 'Report number',
  'inspection.date': 'Date of the visit',
  'inspection.inspector': 'Inspector',
  'inspection.result': 'Result',
  'inspection.conform': 'Compliant',
  'inspection.notConform': 'Not compliant',
  'inspection.deadline': 'Deadline for the works',
  'inspection.pages': 'Report pages',
  'inspection.remarksFound': '{n} remark(s) read — untick those to leave out',
  'inspection.addToProject': 'Add to the project ({n})',
  'inspection.txAdd': 'Inspection report',
  'inspection.remarks': 'Remarks — {open} to fix of {n}',
  'inspection.remarksTitle': 'Inspection remarks',
  'inspection.openCount': '{n} to fix of {total}',
  'inspection.pack': 'Re-inspection pack (PDF)',
  'inspection.packFile': 're-inspection pack',
  'inspection.delete': 'Delete the report',
  'inspection.deleteConfirm': 'The report and its {n} remark(s) will be removed from the project.',
  'remark.one': 'remark',
  'remark.add': 'Add a remark',
  'remark.open': 'To fix',
  'remark.fixed': 'Fixed',
  'remark.text': 'Remark',
  'remark.article': 'Article',
  'remark.ref': 'Circuit or point',
  'remark.fixNote': 'What was done',
  'remark.fixNoteHint': 'E.g. 30 mA type A RCD added on circuit B',
  'remark.photos': 'Photos of the fix',
  'remark.delete': 'Delete the remark',
  'invoice.menu': 'Invoices…',
  'invoice.title': 'Invoices',
  'invoice.intro':
    'Issued invoices stay in the project and are not changed afterwards. Each one downloads as a PDF (with a payment QR code) and, between businesses, as a Peppol (UBL) file for the e-invoicing mandatory in Belgium since 2026.',
  'invoice.none': 'No invoice for this project yet.',
  'invoice.new': 'New invoice',
  'invoice.issue': 'Issue the invoice',
  'invoice.txIssue': 'Invoice {n}',
  'invoice.duplicate': 'Number {n} is already used in this project.',
  'invoice.file': 'Invoice',
  'invoice.seller': 'Your details',
  'invoice.sellerKept': '(kept on this device)',
  'invoice.buyer': 'Client',
  'invoice.buyerHint':
    'Private client: leave the VAT number empty, the invoice goes as a PDF. Business: its enterprise number is its Peppol id (0208).',
  'invoice.details': 'Invoice',
  'invoice.name': 'Name',
  'invoice.vatNumber': 'VAT number',
  'invoice.street': 'Street and number',
  'invoice.postcode': 'Postcode',
  'invoice.city': 'City',
  'invoice.country': 'Country',
  'invoice.email': 'Email',
  'invoice.peppolId': 'Peppol id (optional)',
  'invoice.number': 'No.',
  'invoice.vat': 'VAT',
  'invoice.reverseCharge': 'Reverse charge (contractor)',
  'invoice.issueDate': 'Date',
  'invoice.dueDate': 'Due date',
  'invoice.orderRef': 'Client reference (order, quote)',
  'invoice.paymentRef': 'Structured communication',
  'invoice.note': 'Note',
  'invoice.lines': 'Lines',
  'invoice.addQuoteLines': 'Add the quote lines',
  'invoice.addLine': 'Line',
  'invoice.unit': 'Unit',
  'invoice.unit.C62': 'piece',
  'invoice.unit.MTR': 'metre',
  'invoice.unit.HUR': 'hour',
  'invoice.amount': 'Amount',
  'invoice.total': 'Total incl. VAT',
  'invoice.peppolReady':
    '✓ Ready for Peppol: the UBL file can be sent through your Peppol access point or the Hermes portal.',
  'invoice.pdfOnly': 'PDF invoice (client without a Belgian enterprise number: no Peppol sending).',
  'invoice.peppolHint': 'Peppol BIS Billing 3.0 (UBL) file to send through your access point',
  'invoice.peppolMissing': 'The client has no Peppol id (Belgian enterprise number)',
  'invoice.problem.sellerName': 'Your name is missing.',
  'invoice.problem.sellerVat': 'Your VAT number is missing or not valid.',
  'invoice.problem.sellerAddress': 'Your address is incomplete.',
  'invoice.problem.sellerIban': 'The IBAN is not valid.',
  'invoice.problem.buyerName': 'The client’s name is missing.',
  'invoice.problem.buyerAddress': 'The client’s address is missing.',
  'invoice.problem.buyerVatReverse': 'Reverse charge needs the client’s VAT number.',
  'invoice.problem.buyerVat': 'The client’s VAT number is not valid.',
  'invoice.problem.noLines': 'The invoice has no lines.',
  'invoice.problem.lineDescription': 'A line has no description.',
  'invoice.problem.number': 'The invoice number is missing.',
  'invoice.problem.dates': 'The dates are not valid (due date before the invoice date?).',
  'invoice.problem.paymentRef': 'The structured communication is not valid.',

  // Guided tutorial (Tour.tsx, tourSteps.ts)
  'tour.menu': 'Guided tutorial',
  'tour.welcome': 'Take the guided tour',
  'tour.begin': 'Start',
  'tour.next': 'Next',
  'tour.back': 'Back',
  'tour.replay': 'Show again',
  'tour.skipDemo': 'Skip demo',
  'tour.restart': 'Start again',
  'tour.finish': 'Finish',
  'tour.close': 'Close the tutorial',
  'tour.playPart': 'Show this part again',
  'tour.prevPart': 'Previous part',
  'tour.nextPart': 'Next part',
  'tour.cap.aiBoard': 'Read a board from a photo',
  'tour.cap.ib': 'Design current of the lighting circuit',
  'tour.sample.circuit': 'Kitchen',
  'side.label': 'Sidebar',
  'side.fold': 'Fold the sidebar',
  'side.unfold': 'Unfold the sidebar',
  'palette.fold': 'Fold the symbols panel',
  'palette.unfold': 'Unfold the symbols panel',
  'side.assistant': 'AI assistant',
  'side.selectHint': 'Click a symbol or a wire on the sheet to see and change its properties here.',
  'side.assistHint':
    'Ask for a change in your own words, for example “add an EV charger on 32 A, then generate the multi-line diagram”. You see what will change before you apply it.',
  'side.assistSignedOut':
    'The assistant changes the drawing from a request in your own words. Sign in to use it.',
  'tour.area.assistant': 'The AI assistant',
  'tour.area.rail': 'One icon per section',
  'tour.cap.assistant': 'Opened by AI → Assistant',
  'tour.cap.assistSend': 'Send, then check the proposal',
  'tour.cap.assistSignIn': 'The assistant needs an account',
  'tour.sample.assist': 'Add an EV charger on 32 A, then generate the multi-line diagram',
  'tour.tipLabel': 'Best practice',
  'tour.chapter': 'Part {n}/{total}',
  'tour.ch1': 'Start a project',
  'tour.ch2': 'Draw by hand',
  'tour.ch3': 'The circuit table',
  'tour.ch4': 'The check',
  'tour.ch5': 'More diagrams',
  'tour.ch6': 'Save and hand over',
  'tour.sample.title': 'Dupont house',
  'tour.sample.owner': 'Marie Dupont',
  'tour.sample.address': '12 Rue de la Station, 1300 Wavre',
  'tour.area.toolbar': 'Menus and tools',
  'tour.area.palette': 'Symbols',
  'tour.area.canvas': 'The sheet',
  'tour.area.inspector': 'Sidebar: selection, AI assistant, sheet',
  'tour.area.sheets': 'Sheets',
  'tour.area.status': 'Status and check counter',
  'tour.cap.template': 'House, apartment or workshop, ready-made',
  'tour.cap.survey': 'From what you recorded on site',
  'tour.cap.example': 'A complete house to explore',
  'tour.cap.standard': 'The rules the check applies',
  'tour.cap.locale': 'The language of the drawings',
  'tour.cap.icc': 'Needed to check breaking capacities',
  'tour.cap.titleBlock': 'The title block fills itself',
  'tour.cap.search': 'Find a symbol by name',
  'tour.cap.tag': 'Tagged W1 automatically',
  'tour.cap.label': 'The label follows the properties',
  'tour.cap.drop': 'Voltage drop against its limit',
  'tour.cap.codes': 'Circuit codes in order',
  'tour.cap.statusChecks': 'Always visible',
  'tour.cap.banner': 'Generated from the single-line diagram',
  'tour.cap.sheetKinds': 'Kinds of sheet',
  'tour.cap.revisions': 'Freeze revision A, B…',
  'tour.cap.exportFull': 'The full handover PDF',
  'tour.cap.quote': 'Quote and bill of materials',
  'tour.cap.cloud': 'Sync, team and sharing',

  'tour.intro.title': 'Draw your first installation',
  'tour.intro.body':
    'In about ten minutes you will start a project and draw a small single-line diagram, then let Tablo do the rest: circuits, calculations, the RGIE check and the multi-line diagram.\nEach step **highlights** part of the screen and shows it, one part at a time: the **‹ ›** arrows — or the **← →** keys — play the previous or next part. At the end of a step every paragraph is listed with its best practice; click one to see it again. Try things yourself between parts, then click **Next**.\nThe tutorial works in a new, empty project: keep it or throw it away at the end.',
  'tour.intro.tip':
    'Tablo is made for a computer with a mouse and keyboard. On a phone it opens the **site survey** instead: record the rooms and points on site, then finish the drawings here.',

  'tour.workspace.title': 'Your workspace',
  'tour.workspace.p1':
    '**Top bar**: the menus (File, Diagram, Inspection, AI), undo, the tools, and the Circuits and RGIE check panels.',
  'tour.workspace.p2': '**Symbols** on the left: the symbols for the kind of sheet you are on.',
  'tour.workspace.p3':
    '**The sheet** in the middle, a real drawing frame with its title block. Wheel or pinch to zoom, middle button or Space + drag to pan, **F** to fit.',
  'tour.workspace.p4':
    'The **sidebar** on the right is made of sections that fold open and shut: **Selection** (the properties of what is selected), the **AI assistant** and **Sheet** (name, kind, paper, project information).',
  'tour.workspace.p5':
    'The button at its top folds the sidebar to a rail of icons, for more room on the sheet. Click an icon to show its section; click it again, or the sheet, to put it away.',
  'tour.workspace.p6': '**Sheets** at the bottom, then the **status bar** with the check counter.',
  'tour.workspace.tip':
    'Learn the one-key tools early — **V** select, **W** wire, **T** text, **R** rotate, **F** fit. Left hand on the keyboard, right hand on the mouse is the fastest way to draw.',

  'tour.start.title': 'Three ways to start',
  'tour.start.p1':
    '**File → New from template**: a house, an apartment or a three-phase workshop, board already drawn.',
  'tour.start.p2': '**File → Site survey**: what you recorded on the phone becomes the diagrams.',
  'tour.start.p3': '**File → Example**: a complete house to look around in.',
  'tour.start.p4': '**AI → Import a board photo** reads an existing board, device by device.',
  'tour.start.tip':
    'For a real job, don’t start from an empty sheet: start from a template or from your site survey, then adjust. The board is laid out the RGIE way from the first minute.',

  'tour.info.title': 'Project information first',
  'tour.info.p1':
    '**File → Project information** fills the title block of every sheet: project, owner, installation address, author.',
  'tour.info.p2': 'The **standard** decides which rules the check applies: RGIE, NF C 15-100 or NEN 1010.',
  'tour.info.p3':
    'The **language of the drawings** can differ from the interface: a French interface for a Flemish client, say.',
  'tour.info.p4':
    'The earthing system, the supply voltage and the **prospective short-circuit current** at the origin.',
  'tour.info.p5': '**OK**: the title block of every sheet fills itself.',
  'tour.info.tip':
    'Fill it in before you draw. Enter the short-circuit current at the origin, or breaking capacities can’t be checked, and draw in the client’s language: that is what the client and the inspector will read.',

  'tour.palette.title': 'The symbol palette',
  'tour.palette.p1':
    'Symbols are grouped by family (distribution, protection, outlets, lighting…) and filtered for the kind of sheet. Type in the search box to find one by name.',
  'tour.palette.p2':
    'Click a symbol, then click on the sheet — here, the **supply**. It gets its tag, W1, straight away.',
  'tour.palette.tip':
    'The palette only offers what belongs on this kind of sheet. Tick **All symbols** only when you really need one from elsewhere, and make anything missing with **New symbol…** at the bottom rather than drawing it with loose lines.',

  'tour.place.title': 'Place the devices',
  'tour.place.p1':
    'Place the board from top to bottom: an **RCD** (Q1), a **breaker** (Q2) and a **socket** (X1). Everything snaps to a 2.5 mm grid.',
  'tour.place.p2':
    '**R** rotates and **M** mirrors the symbol being placed or the selection; **Esc** puts the tool down.',
  'tour.place.tip':
    'Draw the board the way it reads: supply, main switch or RCD, breakers, then the points. Keep the tags Tablo gives: the multi-line diagram, the circuit list and the board labels rely on them.',

  'tour.wire.title': 'Connect with wires',
  'tour.wire.p1':
    'Press **W**, click a **port** — the end of a symbol lights up when you are on it — then the next port.',
  'tour.wire.p2': 'Corners are routed for you: wire the RCD to the breaker, then the breaker to the socket.',
  'tour.wire.p3':
    'Click on empty grid to add a corner yourself; start or end on a wire to make a T-junction. **Backspace** removes the last corner, **Esc** cancels.',
  'tour.wire.tip':
    'Always end a wire on a port or on another wire, never just next to it. The connections — not the picture — are what the circuits, the calculations and the check read: a wire that only looks connected isn’t.',

  'tour.inspector.title': 'Set the properties',
  'tour.inspector.p1':
    'With the select tool (**V**), click a device: the **Selection** section of the sidebar shows its tag and everything it carries — rating, curve, sensitivity, cable, catalogue reference, photos.',
  'tour.inspector.p2':
    'Here the breaker becomes **20 A, curve C**, and its label on the sheet follows. Double-click a device to jump to its first field.',
  'tour.inspector.tip':
    'Enter real values, not placeholders. Ratings, curves, RCD sensitivity and cables feed the calculations and the check: nothing on a Tablo drawing is only decoration.',

  'tour.edit.title': 'Move, copy, undo',
  'tour.edit.p1': 'Drag a device to move it: its wires follow.',
  'tour.edit.p2': '**{mod}Z** undoes — the socket goes back. **{mod}⇧Z** redoes.',
  'tour.edit.p3':
    'Drag on empty space to select an area (left to right: what is fully inside; right to left: everything it touches). **Shift**-click adds, **{mod}D** duplicates, the arrows nudge by 2.5 mm (10 with Shift), **Del** deletes.',
  'tour.edit.tip':
    'Undo keeps the last 500 steps, so experiment freely. Still, save before big changes such as generating sheets or renumbering: the file is your safety net, not the undo list.',

  'tour.circuits.title': 'The circuit table',
  'tour.circuits.p1':
    '**Circuits** opens the table of the board: one row per breaker, with its description, rating, curve, RCD, cable, section, length, installation method and points.',
  'tour.circuits.p2':
    'Change a cell and the drawing follows — here, a description. The table and the sheet are two views of the same board.',
  'tour.circuits.tip':
    'This is the fastest and safest way to build a single-line diagram. Draw the head of the board by hand if you like, then add and edit the circuits here instead of drawing each one.',

  'tour.addCircuit.title': 'Add a circuit in one click',
  'tour.addCircuit.p1':
    'Choose the RCD the circuit goes under and its kind — sockets, lighting or one appliance — then **Add circuit**.',
  'tour.addCircuit.p2':
    'Tablo draws the breaker, the points and the bus, makes room and fills in the usual values: 16 A / 1.5 mm² for lighting, 20 A / 2.5 mm² for sockets.',
  'tour.addCircuit.tip':
    'Put each circuit under the right RCD from the start: bathroom, washing machine, dishwasher and dryer go under the 30 mA one. Keep socket circuits to 8 points at most — the table counts them for you.',

  'tour.calc.title': 'Calculations as you type',
  'tour.calc.p1':
    '**Ib** is the design current; **Iz**, what the cable can carry for its insulation and installation method.',
  'tour.calc.p2':
    '**ΔU** is the voltage drop (usually 3 % for lighting, 5 % for the rest). With 28 m of 1.5 mm², the lighting circuit fails: the value turns red.',
  'tour.calc.p3':
    '**Max L** is the longest cable still protected against short circuits. Any value in red fails.',
  'tour.calc.tip':
    'Enter the real cable lengths, measured on the plan or on site: the voltage drop and the maximum length are only as good as these numbers. They are design aids — the inspection’s measurements still decide.',

  'tour.renumber.title': 'Number the circuits',
  'tour.renumber.p1':
    '**Number circuits** puts the codes back in order — A1, A2, A3… — after circuits were added, moved or removed.',
  'tour.renumber.p2':
    'Each point carries its circuit’s code, the same on the single-line diagram, the position plan and the board labels.',
  'tour.renumber.tip':
    'Renumber once the board is settled and before you make plans, labels and the handover pack — not after. Codes that change once the documents are on site cause mistakes.',

  'tour.assistant.title': 'Ask the AI assistant',
  'tour.assistant.p1':
    '**AI → Assistant** opens the **AI assistant** section of the sidebar. It stays there while you draw, and keeps the conversation.',
  'tour.assistant.p2':
    'Type or dictate a request in your own words — several changes at once if you like — then **Send**.',
  'tour.assistant.p3':
    'Each answer lists the changes it proposes and what the RGIE check would say about them. **Apply** makes them in one go, as a single undo step.',
  'tour.assistant.tip':
    'Read the proposal before you apply it, and check the result on the sheet: the assistant drafts, you sign. One clear request at a time gives the best results.',
  'tour.checks.title': 'The RGIE check',
  'tour.checks.p1': 'The **RGIE check** panel lists errors, warnings and notes, updated with every change.',
  'tour.checks.p2':
    'Each one names the rule and says why and how to fix it; click it to select what it is about. Rules marked **to confirm** come from secondary sources.',
  'tour.checks.p3': 'The counter in the status bar is always in view.',
  'tour.checks.tip':
    'Watch the counter while you draw and fix things as they appear; aim for zero errors before you export. The check is an aid — the approved inspection body still decides.',

  'tour.multiline.title': 'Generate the multi-line diagram',
  'tour.multiline.p1':
    '**Diagram → Generate the multi-line diagram** builds it from the single-line one on a new sheet: incoming strip X0, main switch, RCDs, bus bars, breakers and outgoing strip X1.',
  'tour.multiline.p2': 'When the single-line diagram changes, this banner offers **Update**.',
  'tour.multiline.p3':
    'The same menu numbers wires and terminals, assigns catalogue references and lays out the board.',
  'tour.multiline.tip':
    'Never redraw what Tablo can generate. Make the change on the single-line diagram and click **Update**: hand edits on a generated sheet are replaced.',

  'tour.sheets.title': 'One project, many sheets',
  'tour.sheets.p1':
    'The **+** after the tabs adds a sheet: single-line, multi-line, **control diagram** (coils and contacts linked by their tag) or **position plan** (import the floor plan as PDF, image or DXF, set its scale, draw rooms with **P**, place the points).',
  'tour.sheets.p2': 'Click a tab to switch sheets; double-click it to rename it.',
  'tour.sheets.tip':
    'Keep one installation in one project. On the position plan, place points from the **To place** list rather than the palette: they keep their circuit’s code, so the plan and the diagram match, as the RGIE requires.',

  'tour.save.title': 'Save and hand over',
  'tour.save.p1': '**{mod}S** saves a **.tablo** file with everything in it, plans and photos included.',
  'tour.save.p2':
    '**Revisions** freezes the project as A, B… with a description, shown in the title block; **Compare** shows what changed.',
  'tour.save.p3':
    '**Export — PDF + circuit list** makes the full handover set; the quote, labels and reports are in the same menu.',
  'tour.save.p4': '**Sign in** to sync your devices, work as a team and share a read-only link.',
  'tour.save.tip':
    'Autosave only rescues you after a crash; it is not storage. Save the file or put the project in the cloud, and freeze a revision every time a drawing leaves your hands, so you can always say what changed since.',

  'tour.done.title': 'You are ready',
  'tour.done.body':
    'You started a project, drew a board by hand and with the circuit table, read the calculations and the check, and generated the multi-line diagram. The habits that pay off:\n- start from a template or a site survey;\n- fill in the project information first;\n- end wires on ports and enter real values;\n- build circuits in the table, renumber before handover;\n- generate, don’t redraw, and click Update;\n- save the file and freeze a revision for every issue.',
  'tour.done.tip':
    'Open **File → Example** to see a complete house, or **File → New from template** for your first real job. After the job, the **Inspection** menu takes the test results, the inspection report and the invoice. This tutorial stays in **File → Guided tutorial**.',
};
